import { localizedPageTitle } from '../localization/page-titles';
import { requestLanguage } from '../localization/request-language';
export type AnalyticsParams = Record<string, string | number | boolean | undefined>;
const measurementId = 'G-DK8FR2RYZ8';
const allowed = new Set(['membership_level', 'access_mode', 'book_id', 'book_title', 'menu_id', 'menu_name', 'parent_menu_id', 'source', 'outcome', 'reason', 'position_seconds', 'duration_seconds', 'position_percent', 'listening_seconds', 'audio_seconds', 'playback_rate', 'milestone', 'elapsed_ms', 'enabled', 'timer', 'feature']);
const metaEvents = new Set(['selfie_requested', 'selfie_created', 'audiobook_play_click', 'audiobook_started', 'audiobook_upgrade_click', 'audiobook_retailer_click', 'audiobook_author_site_click']);
['character_id', 'character_name', 'chat_mode', 'image_request_type'].forEach(key => allowed.add(key));
const characters = new Map<string, string>();
let lastPage = '';

export function reportingPageTitle(title: string, language: 'en' | 'de'): string {
  if (language === 'en') return title;
  const suffix = ' | Bookish Boyfriend';
  const hasSuffix = title.endsWith(suffix);
  const label = hasSuffix ? title.slice(0, -suffix.length) : title;
  return `${label} [${language.toUpperCase()}]${hasSuffix ? suffix : ''}`;
}

export function pageDetails(path = window.location.pathname, search = window.location.search) {
  const section = (path.split('/')[1] || '').toLowerCase();
  const titles: Record<string, string> = {
    '': 'Home', chats: 'Chats', saved: 'Saved Characters', price: 'Membership Plans',
    audiobooks: 'Audiobooks', setting: 'Settings', login: 'Log In', signup: 'Sign Up',
    support: 'Support', terms: 'Terms of Service', privacy: 'Privacy Policy',
    'successful-payment': 'Payment Complete',
  };
  const landingSlug = section === 'audiobook' && path.split('/')[2] ? path.split('/')[2] : undefined;
  const id = section === 'chat' ? path.split('/')[2] : undefined;
  const name = id ? characters.get(id) : undefined;
  const bookTalk = new URLSearchParams(search).get('bookTalk') === 'true';
  const context: AnalyticsParams = id && name ? {
    character_id: id, character_name: name, chat_mode: bookTalk ? 'book_talk' : 'chat',
  } : {};
  const title = landingSlug ? 'Audiobook' : id ? `${bookTalk ? 'Book Talk' : 'Chat'}${name ? ` with ${name}` : ''}` : titles[section] || 'Bookish Boyfriend';
  return {
    title: title === 'Bookish Boyfriend' ? title : `${title} | Bookish Boyfriend`,
    path: landingSlug ? `/audiobook/${landingSlug}` : id && name ? `/chat/${encodeURIComponent(id)}${bookTalk ? '/book-talk' : ''}` : (section in titles ? `/${section}` : '/'),
    pending: Boolean(id && !name), context,
    excluded: section === 'admin' || section === 'callback',
  };
}

// Resolve public character metadata, never a user's conversation title or messages.
export function registerAnalyticsCharacter(id: string, name: string) {
  if (!id || !name) return;
  characters.set(id, name.slice(0, 100));
  if (window.location.pathname.split('/')[2] === id) trackPageView();
}

export function trackPageView() {
  const page = pageDetails();
  if (page.excluded) { lastPage = ''; return; }
  const language = requestLanguage();
  document.title = localizedPageTitle(page.title, language);
  if (page.pending) { lastPage = ''; return; }
  // Conversation IDs and incidental query updates are not new content pages.
  const pageKey = `${page.path}:${language}`;
  if (lastPage === pageKey) return;
  lastPage = pageKey;
  track('page_view', { source: analyticsPage(window.location.pathname), ...page.context });
}

export function analyticsPage(path: string): string {
  const page = (path.split('/')[1] || '').toLowerCase();
  return ['chat', 'chats', 'saved', 'price', 'audiobook', 'audiobooks', 'setting', 'login', 'signup', 'support'].includes(page) ? page : 'home';
}

export function membershipLevel(plan?: string): string {
  switch (String(plan || '').toLowerCase()) {
    case 'premium': return 'premium';
    case 'plus': case 'patron': return 'patron';
    case 'basic': case 'free': case 'subscriber': return 'subscriber';
    case 'guest': return 'guest';
    default: return 'unknown';
  }
}

// Analytics must never block the feature or receive prompts, identities, signed URLs or raw errors.
export function track(name: string, params: AnalyticsParams = {}) {
  try {
    if (!['bookishboyfriend.com', 'www.bookishboyfriend.com'].includes(window.location.hostname)) return;
    if (navigator.doNotTrack === '1' || (navigator as any).globalPrivacyControl === true) return;
    const page = pageDetails();
    if (page.excluded) return;
    const language = requestLanguage();
    const clean: AnalyticsParams = {};
    for (const [key, value] of Object.entries({ ...page.context, ...params })) {
      if (!allowed.has(key) || value === undefined) continue;
      if (typeof value === 'number' && Number.isFinite(value)) clean[key] = Math.round(value * 100) / 100;
      else if (typeof value === 'boolean') clean[key] = value;
      else if (typeof value === 'string') clean[key] = value.slice(0, 100);
    }
    // Selected site language, not browser language or caller-supplied metadata.
    clean.site_language = language;
    const host = window as any;
    const event = `bb_${name}`;
    const reportTitle = reportingPageTitle(page.title, language);
    const pageViewFields = name === 'page_view' ? { page_title: reportTitle } : {};
    // Nested payload avoids stale book/menu parameters persisting into later GTM events.
    try { (host.dataLayer = host.dataLayer || []).push({ event, bb: { ...clean, ...pageViewFields }, bb_delivery: 'direct', ...pageViewFields }); } catch {}
    if (name === 'page_view') {
      try { host.gtag?.('set', { page_title: reportTitle, site_language: language }); } catch {}
    }
    try {
      host.gtag?.('event', name === 'page_view' ? 'page_view' : event, { ...clean, send_to: measurementId,
        page_location: `${window.location.origin}${page.path}${language === 'de' ? '?lang=de' : ''}`,
        page_referrer: '', page_title: reportTitle, transport_type: 'beacon',
        allow_google_signals: false, allow_ad_personalization_signals: false });
    } catch {}
    // Reading choices can be sensitive: no book, genre, character, or listening details to advertising.
    if (metaEvents.has(name)) {
      try { host.fbq?.('trackCustom', event, { membership_level: clean.membership_level || 'unknown' }); } catch {}
    }
  } catch {}
}

export function imageFailure(error: any): string {
  if (error?.image_limit || error?.monthly_limit || error?.monthly_limit_with_bonus || error?.exhausted_current_subscription) return 'quota';
  if (error?.premium_only || error?.pg_only || error?.upgrade_needed || error?.plus_trail_ended) return 'membership';
  if (error?.image_moderated) return 'provider_rejected';
  return 'request_failed';
}

// The HTML pixel already sends PageView on a direct page load. Send one only
// when the SPA enters a landing route without a reload.
export function createLandingPixelTracker(initialRoute: string) {
  let first = true;
  let current = '';
  return {
    view(path: string): boolean {
      if (current === path) return false;
      current = path;
      if (first) {
        first = false;
        if (path === initialRoute) return false;
      }
      return true;
    },
    leave(): void { current = ''; },
  };
}
const landingPixelTracker = createLandingPixelTracker(window.location.pathname);
export function trackLandingPixelView() {
  const path = window.location.pathname;
  if (!path.toLowerCase().startsWith('/audiobook/') || path.split('/').filter(Boolean).length !== 2) return;
  if (!['bookishboyfriend.com', 'www.bookishboyfriend.com'].includes(window.location.hostname)) return;
  if (navigator.doNotTrack === '1' || (navigator as any).globalPrivacyControl === true) return;
  if (!landingPixelTracker.view(path)) return;
  try { (window as any).fbq?.('track', 'PageView'); } catch {}
}
export function leaveLandingPixelView() { landingPixelTracker.leave(); }
